import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {HeadingNode} from '@lexical/rich-text';
import {$isTableNode} from '@lexical/table';
import {$createPlaceholderNode} from 'components/DSYS/editor/nodes/PlaceholderNode';
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  LexicalEditor,
  LexicalNode,
  RangeSelection,
  REMOVE_TEXT_COMMAND,
} from 'lexical';
import {useEffect} from 'react';

import {$isEnhancedHeadingNode} from './EnhancedHeadingNode';
import {useIsTemplateMode} from './RoleContext';
import {findSectionRangeForHeading} from './SectionRangeUtils';

const PROTECTED_HEADING_TAGS = ['h1'];

/**
 * Check if a heading node should be protected based on its tag
 */
const isProtectedHeading = (node: LexicalNode): node is HeadingNode => {
  if (!$isEnhancedHeadingNode(node)) return false;
  const tag = node.getTag();
  return PROTECTED_HEADING_TAGS.includes(tag);
};

/**
 * Plugin that protects enhanced headings from deletion in document mode.
 * Instead of preventing all deletion, this allows deletion to proceed but
 * preserves heading nodes while removing other content.
 */
export const HeadingProtectionPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const isTemplateMode = useIsTemplateMode();

  useEffect(() => {
    if (isTemplateMode) {
      return;
    }

    return registerHeadingProtection(editor);
  }, [editor, isTemplateMode]);

  return null;
};

export function registerHeadingProtection(editor: LexicalEditor) {
  /**
   * After any deletion, check if headings need placeholders
   */
  const addPlaceholdersToEmptyHeadings = () => {
    editor.update(() => {
      const root = editor.getEditorState()._nodeMap;
      const allNodes = Array.from(root.values());

      allNodes.forEach((node) => {
        if (!isProtectedHeading(node)) return;

        const sectionRange = findSectionRangeForHeading(node);

        // If section has no content, check if there's already a placeholder
        if (sectionRange.contentKeys.length === 0) {
          // Check if next sibling is already a placeholder paragraph
          const nextSibling = node.getNextSibling();
          if (nextSibling && 'getChildren' in nextSibling) {
            // Check if it's a paragraph with placeholder content
            const children = (nextSibling as any).getChildren();
            const hasPlaceholder = children.some(
              (child: LexicalNode) => child.getType() === 'placeholder'
            );
            if (hasPlaceholder) return; // Already has placeholder
          }

          // Add placeholder
          const placeholderParagraph = $createParagraphNode();
          const placeholderText = $createTextNode('Start writing…');
          const placeholderNode = $createPlaceholderNode('heading-placeholder');
          placeholderNode.append(placeholderText);
          placeholderParagraph.append(placeholderNode);
          node.insertAfter(placeholderParagraph);
        }
      });
    });
  };

  /**
   * Custom deletion handler that allows deletion to proceed but preserves heading nodes.
   * This is more sophisticated than blocking all deletion commands.
   */
  const handleProtectedDeletion = (): boolean => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return false; // Let normal deletion proceed
    }

    // Get all nodes that would be affected by the deletion
    const nodes = selection.getNodes();

    // Check if any nodes are headings OR if any nodes are children of headings
    const hasHeadingNodes = nodes.some((node) => {
      // Check if node is a protected heading
      if (isProtectedHeading(node)) {
        return true;
      }

      // Check if node has a protected heading parent/ancestor
      let parent = node.getParent();
      while (parent) {
        if (isProtectedHeading(parent)) {
          return true;
        }
        parent = parent.getParent();
      }

      return false;
    });

    // If no headings are directly involved, let normal deletion proceed
    // but schedule a check for empty headings after deletion completes
    if (!hasHeadingNodes) {
      // Schedule placeholder check after this deletion completes
      setTimeout(() => addPlaceholdersToEmptyHeadings(), 0);
      return false;
    }

    // Perform selective deletion - remove non-heading content only
    performSelectiveDeletion(selection);
    return true; // Prevent default deletion behavior
  };

  /**
   * Find the nearest protected heading that a node belongs to (i.e., the heading that precedes this node)
   */
  const findNearestHeadingForNode = (node: LexicalNode): LexicalNode | null => {
    // Get the root and all its children
    const root = node.getTopLevelElement()?.getParent();
    if (!root) return null;

    const allNodes = root.getChildren();
    const nodeIndex = allNodes.findIndex((child) => child.getKey() === node.getKey());

    if (nodeIndex === -1) return null;

    // Look backwards from this node to find the nearest protected heading
    for (let i = nodeIndex - 1; i >= 0; i--) {
      const prevNode = allNodes[i];
      if (isProtectedHeading(prevNode)) {
        return prevNode;
      }
    }

    return null;
  };

  /**
   * Performs deletion that preserves heading nodes and handles content under headings intelligently.
   * - Tables under headings are preserved
   * - Other content under headings is replaced with "Start writing…" placeholder
   */
  const performSelectiveDeletion = (selection: RangeSelection) => {
    const nodes = selection.getNodes();
    const processedHeadings = new Set<string>();
    const preservedNodes = new Set<string>(); // Track nodes that should be preserved
    const nodesToRemove: LexicalNode[] = [];
    const headingsToProcess = new Set<LexicalNode>();

    // First, collect all protected headings that need processing (either selected or have content being deleted)
    nodes.forEach((node: LexicalNode) => {
      if (isProtectedHeading(node)) {
        // Protected heading is directly selected
        headingsToProcess.add(node);
      } else {
        // Check if this node is under a protected heading
        const nearestHeading = findNearestHeadingForNode(node);
        if (nearestHeading && isProtectedHeading(nearestHeading)) {
          headingsToProcess.add(nearestHeading);
        }
      }
    });

    // Process each protected heading that needs attention
    headingsToProcess.forEach((node: LexicalNode) => {
      if (!isProtectedHeading(node)) return;

      const headingKey = node.getKey();

      // Skip if we've already processed this heading
      if (processedHeadings.has(headingKey)) {
        return;
      }

      processedHeadings.add(headingKey);

      // Always preserve the heading itself
      preservedNodes.add(headingKey);

      // Get the section content for this heading
      const sectionRange = findSectionRangeForHeading(node);
      const sectionNodes = sectionRange.contentKeys
        .map((key) => {
          const sectionNode = $getNodeByKey(key);
          return sectionNode;
        })
        .filter(Boolean) as LexicalNode[];

      if (sectionNodes.length === 0) {
        // No content under this heading - add placeholder
        const placeholderParagraph = $createParagraphNode();
        const placeholderText = $createTextNode('Start writing…');
        const placeholderNode = $createPlaceholderNode('heading-placeholder');
        placeholderNode.append(placeholderText);
        placeholderParagraph.append(placeholderNode);
        node.insertAfter(placeholderParagraph);
        return;
      }

      // Check if section contains any tables
      const hasTable = sectionNodes.some((sectionNode) => {
        // Direct table node
        if ($isTableNode(sectionNode)) {
          return true;
        }

        // Check by node type string
        if (sectionNode.getType() === 'table') {
          return true;
        }

        // Check children for tables (in case table is wrapped)
        if ('getChildren' in sectionNode) {
          const children = (sectionNode as any).getChildren();
          const hasTableChild = children.some((child: LexicalNode) => {
            return $isTableNode(child) || child.getType() === 'table';
          });
          if (hasTableChild) return true;
        }

        return false;
      });

      if (hasTable) {
        // Mark all section nodes as preserved, including their descendants
        sectionNodes.forEach((sectionNode) => {
          preservedNodes.add(sectionNode.getKey());

          // Also preserve all descendants of each node (especially important for tables)
          const preserveDescendants = (nodeToPreserve: LexicalNode) => {
            if ('getChildren' in nodeToPreserve) {
              const children = (nodeToPreserve as any).getChildren();
              children.forEach((child: LexicalNode) => {
                preservedNodes.add(child.getKey());
                preserveDescendants(child); // Recursively preserve all descendants
              });
            }
          };

          preserveDescendants(sectionNode);
        });
        return;
      }

      // No tables found - mark all section content for removal and add placeholder
      sectionNodes.forEach((sectionNode) => {
        nodesToRemove.push(sectionNode);
      });

      const placeholderParagraph = $createParagraphNode();
      const placeholderText = $createTextNode('Start writing…');
      const placeholderNode = $createPlaceholderNode('heading-placeholder');
      placeholderNode.append(placeholderText);
      placeholderParagraph.append(placeholderNode);
      node.insertAfter(placeholderParagraph);
    });

    // Second pass: Handle non-heading nodes that aren't preserved
    nodes.forEach((node: LexicalNode) => {
      const nodeKey = node.getKey();

      // Skip if this node is already marked as preserved
      if (preservedNodes.has(nodeKey)) {
        return;
      }

      // Skip protected headings - they're handled in first pass
      if (isProtectedHeading(node)) {
        return;
      }

      // Check if this node is under a protected heading that we've processed
      let isUnderProcessedHeading = false;
      let ancestor = node.getParent();

      while (ancestor) {
        if (isProtectedHeading(ancestor)) {
          if (processedHeadings.has(ancestor.getKey())) {
            isUnderProcessedHeading = true;
          }
          break; // Stop at first protected heading ancestor
        }
        ancestor = ancestor.getParent();
      }

      // Also check using the nearest heading method
      if (!isUnderProcessedHeading) {
        const nearestHeading = findNearestHeadingForNode(node);
        if (nearestHeading && processedHeadings.has(nearestHeading.getKey())) {
          isUnderProcessedHeading = true;
        }
      }

      // If not under a processed heading, mark for removal
      if (!isUnderProcessedHeading) {
        nodesToRemove.push(node);
      }
    });

    // Remove all marked nodes
    nodesToRemove.forEach((node) => {
      if (node.isAttached()) {
        node.remove();
      }
    });

    // Clear the selection after deletion
    $setSelection(null);
  };

  // Register the custom deletion handlers
  const unregisterKeyDelete = editor.registerCommand(
    KEY_DELETE_COMMAND,
    handleProtectedDeletion,
    COMMAND_PRIORITY_HIGH
  );

  const unregisterKeyBackspace = editor.registerCommand(
    KEY_BACKSPACE_COMMAND,
    handleProtectedDeletion,
    COMMAND_PRIORITY_HIGH
  );

  const unregisterRemoveText = editor.registerCommand(
    REMOVE_TEXT_COMMAND,
    handleProtectedDeletion,
    COMMAND_PRIORITY_HIGH
  );

  // Return cleanup function
  return () => {
    unregisterKeyDelete();
    unregisterKeyBackspace();
    unregisterRemoveText();
  };
}
